import React, { useMemo, useState } from 'react';
import T from 'i18n';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Tooltip,
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { FiltersStyles } from 'features/common/Filters/FiltersStyles.js';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentFolder,
  selectDocuments,
  selectDocumentsAssignPermission,
  selectParams,
} from 'common/selectors';
import { Modal } from 'features/common';
import history from 'common/history';
import * as actions from 'features/case/redux/actions';

export const fileAttachmentsAction = {
  add: 'ADD',
  addAll: 'ADD ALL',
  addAllNonImage: 'ADD ALL NON IMAGE',
};

const IMAGE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'svg',
  'avif',
  'bmp',
  'tiff',
  'tif',
  'ico',
  'heic',
  'heif',
]);

const getDocumentIdentifier = (doc = {}) => doc.folderFileId || doc.compositeKey || doc.id;

const AddAttachment = ({ action, show, handleClose, handleSave, file, folder }) => {
  return (
    <Modal
      show={show}
      title={T.translate('case.addAttachmentTitle')}
      handleClose={() => handleClose()}
      handleSave={() => {
        handleSave(action, file);
        handleClose();
      }}
      saveTitle={T.translate('generic.add')}
    >
      {action === fileAttachmentsAction.add
        ? T.translate('case.addAttachmentMsg', { name: file.name, folder })
        : action === fileAttachmentsAction.addAllNonImage
          ? T.translate('case.addAllNonImageAttachmentMsg', { folder })
          : T.translate('case.addAllAttachmentMsg', { folder })}
    </Modal>
  );
};

const folderToPermissionType = ({ type, shared, teamFolder } = {}) => {
  switch (type) {
    case 'trialbook':
      return 'trialbooks';
    case 'court-bundle':
      return teamFolder ? 'my-court-bundles' : 'other-court-bundles';
    case 'bundle':
      return shared ? 'team-bundles' : 'private-bundles';
    case 'team-bundle':
      return 'team-bundles';
    case 'private-bundle':
      return 'private-bundles';
    case 'group-bundle':
      return 'group-bundles';
    default:
      return null;
  }
};

const hasAssignPermissionForFolder = (assignPermission, folder) => {
  if (!assignPermission) return false;
  if (!folder || !folder.id) return false;

  if (Array.isArray(assignPermission)) {
    const permissionKey = folderToPermissionType(folder);
    return permissionKey ? assignPermission.includes(permissionKey) : false;
  }

  return Boolean(assignPermission);
};

const FileAttachments = ({
  attachments,
  handleClickAway,
  parentFileId,
  present,
  headingKey = 'case.fileAttachments',
}) => {
  const classes = FiltersStyles();
  const dispatch = useDispatch();
  const { file: openedFile } = useSelector(selectParams);
  const currentAllDocuments = useSelector(selectDocuments);
  const currentFolder = useSelector(selectCurrentFolder);
  const documentsAssignPermission = useSelector(selectDocumentsAssignPermission);
  const [modalAction, setModalAction] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const isIndexFolder = currentFolder.readOnly && currentFolder.name === 'index';
  const canAssignAttachments = useMemo(
    () => hasAssignPermissionForFolder(documentsAssignPermission, currentFolder),
    [documentsAssignPermission, currentFolder],
  );

  const attachmentsNotInFolder = useMemo(() => {
    const currentDocumentIds = new Set(currentAllDocuments.map(({ id }) => id));
    return attachments.filter(({ id }) => !currentDocumentIds.has(id));
  }, [attachments, currentAllDocuments]);

  const nonImageAttachmentsNotInFolder = useMemo(
    () =>
      attachmentsNotInFolder.filter(
        ({ fileType }) => !IMAGE_EXTENSIONS.has((fileType || '').toLowerCase()),
      ),
    [attachmentsNotInFolder],
  );

  const showAddAllBtn = () => {
    return attachmentsNotInFolder.length > 1;
  };

  const showAddAllNonImageBtn = () => {
    return (
      nonImageAttachmentsNotInFolder.length >= 1 &&
      nonImageAttachmentsNotInFolder.length < attachmentsNotInFolder.length
    );
  };

  const handleOpenFileClicked = (doc, docExistInGrid) => {
    if (docExistInGrid) {
      history.push(
        history.location.pathname.replace(/\/files.*/, ``) +
          `/files/${docExistInGrid.id}${present ? `?present=${present}` : ''}`,
      );
      dispatch(actions.setCurrentSelectedFile(docExistInGrid));
    } else {
      dispatch(actions.fetchFileDetails({ fileId: doc.id, folderFileId: doc.folderFileId })).then(
        (res) => {
          history.push(
            history.location.pathname.replace(/\/files.*/, ``) +
              `/files/${doc.id}${present ? `?present=${present}` : ''}`,
          );
          dispatch(actions.setCurrentSelectedFile({ ...doc, ...res }));
        },
      );
    }
  };

  const assignAttachment = (event, file) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canAssignAttachments) return;
    setShowModal(true);
    setModalAction(fileAttachmentsAction.add);
    setCurrentFile(file);
  };

  const assignAllAttachments = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canAssignAttachments || attachmentsNotInFolder.length <= 1) return;
    setShowModal(true);
    setModalAction(fileAttachmentsAction.addAll);
  };

  const assignAllNonImageAttachments = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canAssignAttachments || nonImageAttachmentsNotInFolder.length < 1) return;
    setShowModal(true);
    setModalAction(fileAttachmentsAction.addAllNonImage);
  };

  const handleClose = () => {
    setShowModal(false);
    setModalAction(null);
    setCurrentFile(null);
    handleClickAway();
  };

  const handleSave = (action, file) => {
    switch (action) {
      case fileAttachmentsAction.addAllNonImage:
      case fileAttachmentsAction.addAll:
      case fileAttachmentsAction.add: {
        const currentFileIdx = currentAllDocuments.findIndex((doc) => doc.id === parentFileId);
        const insertionIndex = currentFileIdx + 1;
        const isBulkAction =
          action === fileAttachmentsAction.addAll ||
          action === fileAttachmentsAction.addAllNonImage;
        const bulkAttachments =
          action === fileAttachmentsAction.addAllNonImage
            ? nonImageAttachmentsNotInFolder
            : attachmentsNotInFolder;
        const documentsToAssign = isBulkAction
          ? bulkAttachments.map(({ id, name }) => ({ id, name }))
          : [file];

        if (!documentsToAssign.length) {
          break;
        }

        dispatch(
          actions.assignDocuments({
            documentIds: documentsToAssign,
            folderId: currentFolder.id,
            isAttachment: true,
            currentFileIdx,
          }),
        )
          .then(() => {
            dispatch(actions.fetchFolder({ folderId: currentFolder.id, noPromise: false })).then(
              (res) => {
                const baseSortPayload = {
                  index: insertionIndex,
                  zeroBasedIndex: currentFolder.zeroBasedIndex,
                  agreed: currentFolder.agreed,
                  etag: res.etag,
                };
                const isSingleFileAssign =
                  action === fileAttachmentsAction.add ||
                  (isBulkAction && documentsToAssign.length === 1);
                const singleFile = action === fileAttachmentsAction.add ? file : bulkAttachments[0];
                const attachmentsToInsert =
                  isBulkAction && !isSingleFileAssign ? bulkAttachments : null;
                const updatedDocumentsPreview =
                  attachmentsToInsert && attachmentsToInsert.length
                    ? [
                        ...currentAllDocuments.slice(0, insertionIndex),
                        ...attachmentsToInsert,
                        ...currentAllDocuments.slice(insertionIndex),
                      ]
                    : null;
                dispatch(
                  actions.updateDocumentSort({
                    ...(isSingleFileAssign
                      ? {
                          ids: [getDocumentIdentifier(singleFile)],
                          singleDroppedDocId: getDocumentIdentifier(singleFile),
                        }
                      : {
                          ids: updatedDocumentsPreview,
                          attachments: attachmentsToInsert,
                          isAttachments: true,
                        }),
                    ...baseSortPayload,
                  }),
                );
              },
            );
          })
          .catch((err) => console.error(err));
        break;
      }
      default:
        break;
    }
  };
  return (
    <>
      <Box>
        <List
          subheader={
            <ListSubheader
              sx={{ lineHeight: '3rem', display: 'flex', justifyContent: 'space-between' }}
            >
              {T.translate(headingKey)}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {!isIndexFolder && showAddAllNonImageBtn() && canAssignAttachments && (
                  <Button type="text" size="small" onClick={(e) => assignAllNonImageAttachments(e)}>
                    {T.translate('generic.addAllNonImageFiles')}
                  </Button>
                )}
                {!isIndexFolder &&
                  attachments.length > 1 &&
                  showAddAllBtn() &&
                  canAssignAttachments && (
                    <Button type="text" size="small" onClick={(e) => assignAllAttachments(e)}>
                      {T.translate('generic.addAll')}
                    </Button>
                  )}
              </Box>
            </ListSubheader>
          }
          className={classes.paper}
          sx={{ minWidth: 400, width: 'max-content' }}
        >
          {attachments.map((doc, index) => {
            const docExistInGrid = currentAllDocuments.find((item) => item.id === doc.id);
            return (
              <ListItem
                key={index}
                sx={{
                  pt: 0,
                  pb: 0,
                  color: 'black',
                  ...(openedFile === doc.id && { backgroundColor: 'rgba(0, 0, 0, 0.1)' }),
                }}
              >
                <ListItemText
                  sx={{ cursor: 'default' }}
                  primary={`${doc.id} - ${doc.name}${
                    docExistInGrid && docExistInGrid.tab ? ` [Tab ${docExistInGrid.tab}]` : ''
                  }`}
                />
                <div>
                  {!docExistInGrid && canAssignAttachments && (
                    <Tooltip
                      title={
                        isIndexFolder
                          ? T.translate('case.addAttachmentIndexFolderTooltipMsg')
                          : T.translate('case.addAttachmentTooltipMsg')
                      }
                      placement="top"
                      arrow
                    >
                      <IconButton
                        size="small"
                        sx={{
                          ...(isIndexFolder && { cursor: 'default !important' }),
                        }}
                        onClick={(e) => {
                          if (!isIndexFolder) {
                            assignAttachment(e, doc);
                          }
                        }}
                      >
                        <AddCircleIcon color={`${isIndexFolder ? 'default' : 'primary'}`} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip
                    title={T.translate('case.viewAttachmentTooltipMsg')}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleOpenFileClicked(doc, docExistInGrid)}
                    >
                      <LaunchIcon sx={{ color: 'black' }} />
                    </IconButton>
                  </Tooltip>
                </div>
              </ListItem>
            );
          })}
        </List>
      </Box>
      {showModal &&
        (modalAction === fileAttachmentsAction.add ||
          modalAction === fileAttachmentsAction.addAll ||
          modalAction === fileAttachmentsAction.addAllNonImage) && (
          <AddAttachment
            show={showModal}
            action={modalAction}
            handleClose={handleClose}
            handleSave={handleSave}
            file={currentFile}
            folder={currentFolder.name}
          />
        )}
    </>
  );
};

export default FileAttachments;
